import React, { useState } from "react";
import { Row, Col, Button, Form, Input, Upload, Steps, message } from "antd";
import { ArrowLeftOutlined, UploadOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import type { UploadProps } from "antd";
import "./Application5.css";
import "../components/StepsTracker.css";

const { Dragger } = Upload;

const Application5: React.FC = () => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const navigate = useNavigate();

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".jpeg,.jpg,.png,.heic,.pdf",
    maxCount: 1,
    fileList,
    beforeUpload: (file) => {
      const isValidFormat = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/heic",
        "application/pdf",
      ].includes(file.type);

      if (!isValidFormat) {
        message.error(
          "Please upload a valid file format (.jpeg, .png, .HEIC or .pdf)",
        );
        return false;
      }

      const isLt10M = file.size / 1024 / 1024 < 10;
      if (!isLt10M) {
        message.error("File must be smaller than 10MB!");
        return false;
      }

      return false; // Prevent automatic upload
    },
    onChange: (info) => {
      setFileList(info.fileList);
    },
    onDrop: (e) => {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  const handleNext = () => {
    // Navigate to Application6 without validation
    navigate("/application6");
  };

  return (
    <div className="application-step-container">
      <div className="application-title-card">
        <div className="title-card-content">
          <div className="city-name-title">City of New Orleans, Louisiana</div>
          <h1 className="program-title-main">E-Bike Incentive Application</h1>
        </div>
      </div>

      <div className="progress-tracker-container">
        <Steps
          current={4}
          className="progress-tracker-steps"
          items={[
            { title: "Review Eligibility" },
            { title: "Confirm Details" },
            { title: "Personal Information" },
            { title: "Upload Documents" },
            { title: "Income Verification" },
            { title: "Survey" },
            { title: "Future Communications" },
            { title: "Terms & Conditions" },
          ]}
        />
      </div>

      <div className="main-step-card">
        <div className="step-header-container">
          <div className="step-badge">
            <div className="step-label">STEP</div>
            <div className="step-number">5</div>
          </div>
          <h2 className="step-title-main">Income Information</h2>
        </div>

        <div className="step-divider" />

        <div className="form-section">
          <Form form={form} layout="vertical" className="income-form">
            {/* Household Income Section */}
            <div className="income-section">
              <h3 className="section-title">Household Income</h3>

              <Row gutter={24}>
                <Col xs={24} lg={12}>
                  <Form.Item
                    name="householdIncome"
                    label="Gross Annual Household Income"
                    className="form-item"
                    rules={[
                      {
                        required: true,
                        message: "Please enter your household income",
                      },
                      {
                        pattern: /^\d+$/,
                        message: "Please enter a valid amount",
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter amount"
                      prefix="$"
                      className="form-input income-input"
                      size="large"
                    />
                  </Form.Item>
                  <div className="income-description">
                    This is your income before taxes, not including any funds
                    received through qualifying programs such as Medicaid, SNAP
                    food assistance, etc.
                  </div>
                </Col>
              </Row>
            </div>

            {/* Upload Income Verification Section */}
            <div className="upload-section">
              <h3 className="section-title">Upload Income Verification</h3>

              <div className="upload-description">
                Attach one of these documents as{" "}
                <span className="underlined-link">Income Verification</span>.
              </div>

              <Form.Item
                name="incomeVerification"
                className="upload-form-item"
                rules={[
                  {
                    required: true,
                    message: "Please upload income verification document",
                  },
                ]}
              >
                <Dragger {...uploadProps} className="upload-dragger">
                  <div className="upload-content">
                    <div className="upload-icon-container">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 25 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="upload-icon"
                      >
                        <path
                          d="M11 18V5.775L7.1 9.675L5 7.5L12.5 0L20 7.5L17.9 9.675L14 5.775V18H11ZM3.5 24C2.675 24 1.96875 23.7063 1.38125 23.1188C0.79375 22.5312 0.5 21.825 0.5 21V16.5H3.5V21H21.5V16.5H24.5V21C24.5 21.825 24.2063 22.5312 23.6188 23.1188C23.0312 23.7063 22.325 24 21.5 24H3.5Z"
                          fill="#777777"
                        />
                      </svg>
                    </div>
                    <div className="upload-text">
                      Click here or drag file to this area to upload.
                    </div>
                    <div className="upload-hint">
                      Acceptable file formats include .jpeg, .png, .HEIC or .pdf
                    </div>
                  </div>
                </Dragger>
              </Form.Item>
            </div>
          </Form>
        </div>
      </div>

      <div className="navigation-buttons">
        <Link to="/application4">
          <Button
            type="default"
            icon={<ArrowLeftOutlined />}
            className="back-button"
          >
            Back
          </Button>
        </Link>
        <Button
          type="primary"
          className="next-button"
          size="large"
          onClick={handleNext}
        >
          Next
        </Button>
      </div>
    </div>
  );
};

export default Application5;
